import { MapPin } from "lucide-react";
import { useEffect, useState } from "react";
import { get, put, type Trip } from "../lib/api.ts";
import { nativeBridge, type Bridge } from "../lib/bridge.ts";
import { Card, ICON, reason } from "../ui/index.ts";

type Consent = { decision: "GRANTED" | "WITHDRAWN" | "NONE"; version: number };

/**
 * Route sharing on the phone, for a trip that is capturing. Someone who never answered (a trip's
 * creator never passes the join step) is asked here, with the same words as the join step (owner
 * rule: everyone's full route is visible to the trip). Someone who already agreed but is not being
 * recorded, for example after reinstalling the app, is recorded again without a second question;
 * that start is what asks Android for the location permission. "No" is respected: nothing is asked.
 */
export function RouteSharing({
  trip,
  bridge = nativeBridge(),
}: {
  trip: Trip;
  bridge?: Bridge | null;
}) {
  const [ask, setAsk] = useState<Consent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const live = !!bridge && trip.state === "ACTIVE";
  const dismissKey = `route-ask-dismissed:${trip.id}`;

  useEffect(() => {
    if (!live || !bridge) return;
    let gone = false;
    void (async () => {
      const [state, consent] = await Promise.all([
        bridge.call("location.state", {}),
        get<Consent>(`/v1/trips/${trip.id}/consents/ROUTE`),
      ]);
      if (gone || (state.active && state.tripId === trip.id)) return;
      if (consent.decision === "GRANTED") await bridge.call("location.start", { tripId: trip.id });
      else if (consent.decision === "NONE" && !sessionStorage.getItem(dismissKey)) setAsk(consent);
    })().catch(() => undefined);
    return () => {
      gone = true;
    };
  }, [live, bridge, trip.id, dismissKey]);

  if (!live || !bridge || !ask) return null;

  async function share() {
    if (!bridge || !ask) return;
    setBusy(true);
    setError(null);
    try {
      await put(`/v1/trips/${trip.id}/consents/ROUTE`, {
        decision: "GRANTED",
        baseVersion: ask.version,
      });
      await bridge.call("location.start", { tripId: trip.id });
      setAsk(null);
    } catch (e) {
      setError(reason(e as Error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Share your route?">
      <p className="small" style={{ margin: 0 }}>
        Your route places your photos and videos on the trip map. Everyone on this trip can see your
        full route. Your phone will ask for location access; choose &ldquo;Allow all the time&rdquo;
        so it keeps recording with the app closed.
      </p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      <div className="row wrap">
        <button className="button primary" type="button" onClick={share} disabled={busy}>
          <MapPin {...ICON} aria-hidden="true" />
          Share my route
        </button>
        <button
          className="button"
          type="button"
          disabled={busy}
          onClick={() => {
            sessionStorage.setItem(dismissKey, "1");
            setAsk(null);
          }}
        >
          Not now
        </button>
      </div>
    </Card>
  );
}
